import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { Visibility } from "../api/types";
import { useServices } from "../app/services";
import { useAction } from "../app/useAction";
import { useResource } from "../app/useResource";
import { Field, PageHeader, ResourceView, Thumb } from "../components/ui";
import { kindLabel, performerNames } from "../lib/labels";

/**
 * おすすめを書く。長文を前提にせず「この曲好き！」だけでもよい。
 * 保存と公開を分ける：「自分だけに保存」と「メンバーに公開」は別のボタン（公開には本人の操作が必要）
 */
export function ComposePage() {
  const [params] = useSearchParams();
  const performanceId = params.get("performance") ?? "";
  const { api, toasts } = useServices();
  const navigate = useNavigate();
  const res = useResource("performances.get", () => api.getPerformance(performanceId), [performanceId]);
  const [body, setBody] = useState("");
  const act = useAction("posts.create", (visibility: Visibility) =>
    api.createPost({ performance_id: performanceId, body: body.trim(), visibility }),
  );

  if (!performanceId) {
    return (
      <div className="page">
        <PageHeader title="おすすめを書く" lead="どの歌唱について書くかを、曲のページから選んでください。" />
        <Link to="/">見つけるへ</Link>
      </div>
    );
  }

  const submit = async (visibility: Visibility) => {
    const r = await act.run(visibility);
    if (r.ok) {
      toasts.show(`${visibility === "members" ? "メンバーに公開しました" : "自分だけに保存しました"}${act.suffix}`);
      void navigate(`/posts/${r.value.id}`, { replace: true });
    }
  };

  return (
    <div className="page page--narrow">
      <PageHeader title="おすすめを書く" lead="ひとことだけでも大丈夫です。" />
      <ResourceView resource={res} what="歌唱">
        {({ performance: p }) => (
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className="compose-target">
                <Thumb videoId={p.segment.video_id} title={p.work.title} size="s" />
                <div>
                  <p className="compose-target__title">{p.work.title}</p>
                  <p className="compose-target__sub">
                    {performerNames(p)} ・ {kindLabel(p.kind)}
                  </p>
                </div>
              </div>
              {!act.connected && <p className="inline-note inline-note--warn">投稿は、まだAPIに接続されていません。</p>}
              <Field label="ひとこと" hint="例：「この日の歌い方がすごく好き。最後のサビのハモりを聴いてほしい」">
                {({ id, describedBy }) => (
                  <textarea
                    id={id}
                    className="input textarea"
                    rows={5}
                    maxLength={4000}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    aria-describedby={describedBy}
                    placeholder="この曲好き！"
                  />
                )}
              </Field>
              <p className="form__note">
                好きな瞬間（時刻）は、VCで聴きながら「ここ好き」を押すと残せます。下書きから公開するときに、この投稿とは別に付けられます。
              </p>
              {act.error && (
                <p className="inline-note inline-note--danger" role="alert">
                  {act.error}
                </p>
              )}
              <div className="form__actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={act.running || !act.connected}
                  onClick={() => submit("private")}
                >
                  自分だけに保存
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={act.running || !act.connected}
                  aria-busy={act.running || undefined}
                  onClick={() => submit("members")}
                >
                  メンバーに公開
                </button>
              </div>
              <p className="form__note">公開すると、このサーバーのメンバーだけが見られます（インターネット全体には公開しません）。</p>
            </form>
          )}
      </ResourceView>
    </div>
  );
}
